import type { components } from '@/api/generated/schema';

type Schemas = components['schemas'];

export type BroadcastScheduleInput = Schemas['PixlideApplicationFeaturesBroadcastsBroadcastScheduleInput'];
export type WeekDays = Schemas['PixlideDomainBroadcastingEnumsWeekDays'];
export type VideoFormat = Schemas['PixlideDomainContentEnumsVideoFormat'];
export type BroadcastStatus = Schemas['PixlideDomainBroadcastingEnumsBroadcastStatus'];

/**
 * Jours de diffusion : champ de bits identique à `WeekDays` côté Domain (lundi = 1 …
 * dimanche = 64). L'ordre est celui d'une semaine française.
 */
export const WEEK_DAYS = [
  { bit: 1, short: 'Lun', label: 'Lundi' },
  { bit: 2, short: 'Mar', label: 'Mardi' },
  { bit: 4, short: 'Mer', label: 'Mercredi' },
  { bit: 8, short: 'Jeu', label: 'Jeudi' },
  { bit: 16, short: 'Ven', label: 'Vendredi' },
  { bit: 32, short: 'Sam', label: 'Samedi' },
  { bit: 64, short: 'Dim', label: 'Dimanche' },
] as const;

const ALL_DAYS = 127;
const WEEKDAYS_ONLY = 31;
const WEEKEND = 96;

/**
 * Combine des jours en valeur `WeekDays`. Le contrat OpenAPI n'énumère que les valeurs
 * NOMMÉES de l'enum (Monday, Weekend…) : une combinaison libre comme lundi + mardi (3) est
 * pourtant valide côté serveur — c'est un `[Flags]`. D'où la conversion explicite.
 */
export function toWeekDays(bits: readonly number[]): WeekDays {
  return bits.reduce((total, bit) => total | bit, 0) as WeekDays;
}

export function fromWeekDays(value: number): number[] {
  return WEEK_DAYS.filter((day) => (value & day.bit) !== 0).map((day) => day.bit);
}

export function describeDays(value: number): string {
  if (value === ALL_DAYS) return 'Tous les jours';
  if (value === WEEKDAYS_ONLY) return 'Du lundi au vendredi';
  if (value === WEEKEND) return 'Le week-end';

  return WEEK_DAYS.filter((day) => (value & day.bit) !== 0)
    .map((day) => day.short)
    .join(', ');
}

/** Bornes d'une journée entière, telles que `TimeWindow.AllDay` côté Domain. */
const DAY_START = '00:00';
const DAY_END = '23:59';

export function isAllDay(startsAt: string, endsAt: string): boolean {
  return startsAt.slice(0, 5) === DAY_START && endsAt.slice(0, 5) === DAY_END;
}

export function describeWindow(startsAt: string, endsAt: string): string {
  if (isAllDay(startsAt, endsAt)) {
    return 'Toute la journée';
  }

  const start = startsAt.slice(0, 5);
  const end = endsAt.slice(0, 5);

  // Plage qui franchit minuit (bar de nuit) : le serveur la gère, on l'explicite au client.
  return end <= start ? `De ${start} à ${end} (le lendemain)` : `De ${start} à ${end}`;
}

/**
 * Compatibilité format ↔ orientation d'écran.
 *
 * DUPLIQUE `BroadcastCompatibilityPolicy` côté Domain, pour griser d'emblée les écrans
 * inadaptés plutôt que de laisser le client découvrir le refus à la validation. Le serveur
 * reste la seule barrière réelle.
 */
export function isFormatCompatible(format: number, orientation: number): boolean {
  const LANDSCAPE = 0;
  const PORTRAIT = 1;

  switch (format) {
    case 0: // 16:9
    case 3: // 21:9
      return orientation === LANDSCAPE;
    case 1: // 9:16
      return orientation === PORTRAIT;
    case 2: // 1:1, acceptable dans les deux orientations
      return true;
    default:
      return false;
  }
}

export const FORMAT_LABELS: Record<number, string> = {
  0: 'Paysage 16:9',
  1: 'Portrait 9:16',
  2: 'Carré 1:1',
  3: 'Bandeau 21:9',
};

/** Statuts de `BroadcastStatus` côté Domain : 0 brouillon, 1 programmée, 2 en pause, 3 terminée. */
export const BROADCAST_STATUS: Record<number, { label: string; tone: 'neutral' | 'success' | 'warning' }> = {
  0: { label: 'Brouillon', tone: 'neutral' },
  1: { label: 'Programmée', tone: 'success' },
  2: { label: 'En pause', tone: 'warning' },
  3: { label: 'Terminée', tone: 'neutral' },
};

/** Transitions de `BroadcastTransition` côté API. */
export const TRANSITION = { publish: 0, pause: 1, resume: 2, end: 3 } as const;

/** Date du jour au format ISO local (`AAAA-MM-JJ`), et non UTC : à 23 h 30, on est encore « aujourd'hui ». */
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${now.getFullYear()}-${month}-${day}`;
}

/*
  Dates du calendrier : toujours des chaînes ISO locales `AAAA-MM-JJ`, comme `startsOn`
  côté API. Les objets `Date` ne servent qu'au calcul, construits à MIDI pour qu'un
  changement d'heure (été/hiver) ne fasse jamais basculer le jour.
*/

function parseIso(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year!, month! - 1, day!, 12);
}

export function addDays(iso: string, count: number): string {
  const date = parseIso(iso);
  date.setDate(date.getDate() + count);

  return todayIso(date);
}

/** Bit `WeekDays` du jour de la semaine d'une date (lundi = 1 … dimanche = 64). */
export function weekDayBit(iso: string): number {
  // getDay() : 0 = dimanche. On décale pour partir du lundi, comme une semaine française.
  const mondayFirst = (parseIso(iso).getDay() + 6) % 7;

  return 1 << mondayFirst;
}

/** Lundi de la semaine contenant la date. */
export function startOfWeek(iso: string): string {
  return addDays(iso, -Math.log2(weekDayBit(iso)));
}

/** Premier jour du mois contenant la date. */
export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function addMonths(iso: string, count: number): string {
  const date = parseIso(startOfMonth(iso));
  date.setMonth(date.getMonth() + count);

  return todayIso(date);
}

/**
 * Grille d'un mois, semaines complètes du lundi au dimanche : les jours des mois voisins
 * comblent la première et la dernière ligne, comme sur un calendrier papier.
 */
export function monthGrid(iso: string): string[] {
  const first = startOfMonth(iso);
  const gridStart = startOfWeek(first);
  const lastOfMonth = addDays(addMonths(first, 1), -1);
  const gridEnd = addDays(startOfWeek(lastOfMonth), 6);

  const days: string[] = [];
  for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
    days.push(day);
  }

  return days;
}

const monthFormatter = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });
const longDayFormatter = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

/** « Juillet 2026 ». */
export function describeMonth(iso: string): string {
  const label = monthFormatter.format(parseIso(iso));

  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** « mardi 16 juillet 2026 ». */
export function describeLongDate(iso: string): string {
  return longDayFormatter.format(parseIso(iso));
}

/** Programmation telle que saisie dans le formulaire. */
export interface ScheduleFormValues {
  readonly startsOn: string;

  /** Vide = diffusion sans date de fin (« jusqu'à nouvel ordre »). */
  readonly endsOn: string;
  readonly allDay: boolean;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly days: readonly number[];
}

export function toScheduleInput(values: ScheduleFormValues): BroadcastScheduleInput {
  return {
    startsOn: values.startsOn,
    endsOn: values.endsOn === '' ? null : values.endsOn,
    startsAt: `${values.allDay ? DAY_START : values.startsAt}:00`,
    endsAt: `${values.allDay ? DAY_END : values.endsAt}:${values.allDay ? '59' : '00'}`,
    days: toWeekDays(values.days),
  };
}
